import { createComponentStore } from '../../utils/createComponentStore';

export const HTTP_METHOD_GET = 'GET';
export const HTTP_METHOD_POST = 'POST';
export const HTTP_METHOD_PUT = 'PUT';
export const HTTP_METHOD_DELETE = 'DELETE';

export const INTERVAL_TYPE_MINUTES = 'minute';
export const INTERVAL_TYPE_HOURS = 'hour';
export const INTERVAL_TYPE_DAYS = 'day';

export const RESPONSE_FORMAT_JSON = 'JSON';
export const RESPONSE_FORMAT_XML = 'XML';
export const RESPONSE_FORMAT_TEXT = 'TEXT';

export const DEFAULT_SUCCESS_CODES = [200, 201, 202, 204];

const keyAuto = () => {
  return 'api_poll_' + (Math.floor(Math.random() * 90000000) + 10000).toString();
};

const initData = () => ({
  outputKey: keyAuto(),
  endpointUrl: '',
  httpMethod: HTTP_METHOD_GET,
  headers: [],
  queryParams: [],
  requestBody: '',
  responseFormat: RESPONSE_FORMAT_JSON,
  pollingInterval: {
    value: 5,
    unit: INTERVAL_TYPE_MINUTES,
  },
  successStatusCodes: [...DEFAULT_SUCCESS_CODES],
});

const onLoadData = (rawData) => {
  const data = { ...initData(), ...rawData };
  // v1 compat: migrate outputPlaceholderKey → outputKey
  if (data.outputPlaceholderKey && !rawData.outputKey) {
    data.outputKey = data.outputPlaceholderKey;
  }
  delete data.outputPlaceholderKey;
  // v1 compat: migrate pollingInterval.type → pollingInterval.unit
  if (data.pollingInterval && data.pollingInterval.type && !rawData.pollingInterval?.unit) {
    data.pollingInterval.unit = data.pollingInterval.type;
  }
  if (data.pollingInterval) delete data.pollingInterval.type;
  if (!data.httpMethod) data.httpMethod = HTTP_METHOD_GET;
  if (!data.successStatusCodes || data.successStatusCodes.length === 0) {
    data.successStatusCodes = [...DEFAULT_SUCCESS_CODES];
  }
  if (!data.responseFormat) data.responseFormat = RESPONSE_FORMAT_JSON;
  if (!data.headers) data.headers = [];
  if (!data.queryParams) data.queryParams = [];
  if (!data.pollingInterval) {
    data.pollingInterval = { value: 5, unit: INTERVAL_TYPE_MINUTES };
  }
  return data;
};

const { createStore, getStore, defaultStore } = createComponentStore({
  type: 'trigger_from_api_polling',
  endpoint: 'trigger_from_api_polling',
  registryKey: 'trigger_from_api_polling',
  initData,
  onLoadData,
  extraActions: (set, get) => ({
    setEndpointUrl: (url) => {
      const data = { ...get().data, endpointUrl: url };
      set({ data });
    },

    setHttpMethod: (method) => {
      const data = { ...get().data, httpMethod: method };
      if (method === HTTP_METHOD_GET || method === HTTP_METHOD_DELETE) {
        data.requestBody = '';
      }
      set({ data });
    },

    setRequestBody: (body) => {
      const data = { ...get().data, requestBody: body };
      set({ data });
    },

    setResponseFormat: (format) => {
      const data = { ...get().data, responseFormat: format };
      set({ data });
    },

    setOutputPlaceholderKey: (value) => {
      const data = { ...get().data, outputKey: value };
      set({ data });
    },

    addHeader: () => {
      const data = { ...get().data, headers: [...get().data.headers, { key: '', value: '' }] };
      set({ data });
    },

    updateHeader: (index, field, value) => {
      const headers = [...get().data.headers];
      if (headers[index]) {
        headers[index] = { ...headers[index], [field]: value };
      }
      set({ data: { ...get().data, headers } });
    },

    removeHeader: (index) => {
      const headers = [...get().data.headers];
      headers.splice(index, 1);
      set({ data: { ...get().data, headers } });
    },

    setHeaders: (headers) => {
      set({ data: { ...get().data, headers: [...headers] } });
    },

    addQueryParam: () => {
      const data = {
        ...get().data,
        queryParams: [...get().data.queryParams, { key: '', value: '' }],
      };
      set({ data });
    },

    updateQueryParam: (index, field, value) => {
      const queryParams = [...get().data.queryParams];
      if (queryParams[index]) {
        queryParams[index] = { ...queryParams[index], [field]: value };
      }
      set({ data: { ...get().data, queryParams } });
    },

    removeQueryParam: (index) => {
      const queryParams = [...get().data.queryParams];
      queryParams.splice(index, 1);
      set({ data: { ...get().data, queryParams } });
    },

    setQueryParams: (params) => {
      set({ data: { ...get().data, queryParams: [...params] } });
    },

    setPollingIntervalValue: (value) => {
      const data = {
        ...get().data,
        pollingInterval: { ...get().data.pollingInterval, value },
      };
      set({ data });
    },

    setPollingIntervalType: (unit) => {
      const data = {
        ...get().data,
        pollingInterval: { ...get().data.pollingInterval, unit },
      };
      set({ data });
    },

    setPollingInterval: (value, unit) => {
      const data = { ...get().data, pollingInterval: { value, unit } };
      set({ data });
    },

    setSuccessStatusCodes: (codes) => {
      const data = { ...get().data, successStatusCodes: [...codes] };
      set({ data });
    },

    addSuccessStatusCode: (code) => {
      const current = get().data.successStatusCodes;
      if (!current.includes(code)) {
        set({ data: { ...get().data, successStatusCodes: [...current, code] } });
      }
    },

    removeSuccessStatusCode: (code) => {
      const current = [...get().data.successStatusCodes];
      const index = current.indexOf(code);
      if (index > -1) {
        current.splice(index, 1);
        set({ data: { ...get().data, successStatusCodes: current } });
      }
    },
  }),
});

export const createTriggerFromApiPollingStore = createStore;
export const getTriggerFromApiPollingStore = getStore;
export default defaultStore;
